import React from "react";
import { 
  FiX, 
  FiTag, 
  FiDollarSign, 
  FiClock, 
  FiType, 
  FiSave,
  FiLoader,
  FiAlertCircle,
  FiTrendingUp,
  FiAward,
  FiFileText,
  FiSettings,
  FiEdit2
} from "react-icons/fi";
import { 
  MdOutlineEdit, 
  MdOutlineAttachMoney,
  MdOutlineAccessTime,
  MdOutlineCategory,
  MdOutlineClose,
  MdOutlineSave,
  MdOutlineAdd,
  MdOutlineLocalOffer,
  MdOutlineDiscount
} from "react-icons/md";
import { IoPricetagOutline, IoTimeOutline, IoCashOutline } from "react-icons/io5";
import InputField from "../../../components/ui/Input";

const EditPlan = ({
  onCancel,
  onSubmit,
  title,
  editPromoData,
  handleONChange,
  loading = false,
}) => {
  // Check if form is valid
  const isFormValid = editPromoData?.title?.trim() && editPromoData?.price && editPromoData?.durationInDays && editPromoData?.type;

  // Get plan type label
  const getPlanTypeLabel = (type) => {
    const typeMap = {
      'banner': 'Banner Plan',
      'boost': 'Boost Plan',
      'advertisement': 'Advertisement Plan'
    };
    return typeMap[type] || type;
  };

  // Get plan type icon
  const getPlanTypeIcon = (type) => {
    const iconMap = {
      'banner': FiFileText,
      'boost': FiTrendingUp,
      'advertisement': FiAward
    };
    return iconMap[type] || FiSettings;
  };

  // Get plan type color
  const getPlanTypeColor = (type) => {
    const colorMap = {
      'banner': 'text-blue-600 bg-blue-50 border-blue-200',
      'boost': 'text-purple-600 bg-purple-50 border-purple-200',
      'advertisement': 'text-emerald-600 bg-emerald-50 border-emerald-200'
    };
    return colorMap[type] || 'text-gray-600 bg-gray-50 border-gray-200';
  };

  const isEditing = editPromoData?._id;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl animate-in zoom-in-95 duration-200 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-[#855DF6]/10 to-[#715EFE]/10 rounded-xl">
              {isEditing ? (
                <MdOutlineEdit className="w-5 h-5 text-[#715EFE]" />
              ) : (
                <MdOutlineAdd className="w-5 h-5 text-[#715EFE]" />
              )}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">
                {isEditing ? "Edit Plan" : "Add New Plan"}
              </h3>
              <p className="text-xs text-gray-400 hidden sm:block">
                {isEditing ? 'Update plan details' : 'Create a new plan'}
              </p>
            </div>
          </div>
          <button
            aria-label="Close modal"
            className="flex items-center justify-center w-9 h-9 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
            onClick={onCancel}
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Plan Type Display (when editing) */}
          {isEditing && editPromoData?.type && (
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200">
              <div className={`p-2 rounded-lg ${getPlanTypeColor(editPromoData.type)}`}>
                {React.createElement(getPlanTypeIcon(editPromoData.type), { className: "w-4 h-4" })}
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500">Current Plan Type</p>
                <p className="text-sm font-semibold text-gray-800">
                  {getPlanTypeLabel(editPromoData.type)}
                </p>
              </div>
            </div>
          )}

          {/* Title */}
          <div>
            <label className="flex items-center gap-2 mb-1.5 text-sm font-medium text-gray-700">
              <FiTag className="w-4 h-4 text-gray-400" />
              <span>Title</span>
              <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <IoPricetagOutline className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                name="title"
                value={editPromoData?.title || ""}
                onChange={handleONChange}
                placeholder="Enter plan title..."
                className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#715EFE]/20 focus:border-[#715EFE] transition-all duration-200"
              />
            </div>
          </div>

          {/* Price */}
          <div>
            <label className="flex items-center gap-2 mb-1.5 text-sm font-medium text-gray-700">
              <FiDollarSign className="w-4 h-4 text-gray-400" />
              <span>Price</span>
              <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <IoCashOutline className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="number"
                name="price"
                value={editPromoData?.price || ""}
                onChange={handleONChange}
                placeholder="Enter price..."
                min="0"
                step="0.01"
                className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#715EFE]/20 focus:border-[#715EFE] transition-all duration-200"
              />
            </div>
          </div>

          {/* Duration */}
          <div>
            <label className="flex items-center gap-2 mb-1.5 text-sm font-medium text-gray-700">
              <FiClock className="w-4 h-4 text-gray-400" />
              <span>Duration (Days)</span>
              <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <IoTimeOutline className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="number"
                name="durationInDays"
                value={editPromoData?.durationInDays || ""}
                onChange={handleONChange}
                placeholder="Enter duration in days..."
                min="1"
                className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#715EFE]/20 focus:border-[#715EFE] transition-all duration-200"
              />
            </div>
          </div>

          {/* Plan Type Selection */}
          <div>
            <label className="flex items-center gap-2 mb-1.5 text-sm font-medium text-gray-700">
              <FiType className="w-4 h-4 text-gray-400" />
              <span>Plan Type</span>
              <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                id="Btype"
                name="type"
                value={editPromoData?.type || ""}
                onChange={handleONChange}
                disabled={isEditing}
                className={`w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#715EFE]/20 focus:border-[#715EFE] transition-all duration-200 appearance-none ${
                  isEditing ? 'bg-gray-50 cursor-not-allowed' : 'bg-white'
                }`}
              >
                <option value="">Select Plan Type</option>
                <option value="banner">Banner Plan</option>
                <option value="boost">Boost Plan</option>
                <option value="advertisement">Advertisement Plan</option>
              </select>
              <FiType className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <FiClock className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
            {isEditing && (
              <p className="mt-1 text-xs text-gray-400">Plan type cannot be changed after creation</p>
            )}
          </div>

          {/* Form Validity Indicator */}
          {!isFormValid && (
            <div className="flex items-center gap-3 p-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl animate-in fade-in duration-200">
              <FiAlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
              <p>Please fill in all required fields</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col-reverse sm:flex-row items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 flex-shrink-0 bg-gray-50/50 rounded-b-2xl">
          <button
            className="w-full sm:w-auto px-6 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-all duration-200"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className={`w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-2.5 text-sm font-medium text-white rounded-lg transition-all duration-200 ${
              isFormValid && !loading
                ? 'bg-gradient-to-r from-[#855DF6] to-[#715EFE] hover:shadow-lg hover:shadow-[#715EFE]/25 hover:-translate-y-0.5'
                : 'bg-gray-300 cursor-not-allowed opacity-70'
            }`}
            onClick={onSubmit}
            disabled={!isFormValid || loading}
          >
            {loading ? (
              <>
                <FiLoader className="w-4 h-4 animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <FiSave className="w-4 h-4" />
                <span>{isEditing ? "Update Plan" : "Create Plan"}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditPlan;